import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Edit, Save, X, Image as ImageIcon, Layers, Share2, 
  AlertCircle, CheckCircle, XCircle, Trash2
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';
import SizeChartEditor from '../../components/SizeChartEditor';
import ColorVariantsEditor, { ColorVariantsSwatchRow } from '../../components/ColorVariantsEditor';
import ColorChangeSection from '../../components/ColorChangeSection';

const MOMENTS = {
  creative_workday: 'Creative Workday',
  unwind: 'Unwind',
  after_hours: 'After Hours',
  escape: 'The Escape',
  gentle_celebration: 'Gentle Celebration'
};

const CATEGORIES = [
  'Dresses', 'Sarees', 'Kurtis', 'Tops', 'Ethnic Wear',
  'Western Wear', 'Party Wear', 'Casual Wear', 'Formal Wear', 'Other'
];

const InventoryDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getProduct(productId);
      setProduct(response.data);
      setEditData({
        product_name: response.data.product_name,
        category: response.data.category,
        description: response.data.description || '',
        selling_price: response.data.selling_price || '',
        mrp: response.data.mrp || '',
        color: response.data.color || '',
        fabric: response.data.fabric || '',
        size_chart: response.data.size_chart || null,
        color_variants: Array.isArray(response.data.color_variants)
          ? response.data.color_variants
          : [],
        color_change_enabled: !!response.data.color_change_enabled,
        color_change_quality: response.data.color_change_quality || 'standard',
      });
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/partners/inventory');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleSave = async () => {
    try {
      setSaving(true);
      // Clean numeric fields  -  empty strings are not valid for Optional[float]
      const payload = {
        ...editData,
        selling_price: editData.selling_price !== '' && editData.selling_price !== null
          ? parseFloat(editData.selling_price) : null,
        mrp: editData.mrp !== '' && editData.mrp !== null
          ? parseFloat(editData.mrp) : null,
      };
      await inventoryAPI.updateProduct(productId, payload);
      toast.success('Product updated');
      setEditing(false);
      fetchProduct();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update product';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
    if (newStatus === 'ACTIVE' && !product.tryon_input_image_key) {
      toast.error('Cannot activate product without a Try-On input image');
      return;
    }

    try {
      await inventoryAPI.updateProduct(productId, { status: newStatus });
      toast.success(`Product ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      fetchProduct();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${product.product_name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await inventoryAPI.deleteProduct(productId);
      toast.success('Product deleted');
      navigate('/partners/inventory');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/partners/inventory"
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{product.product_name}</h1>
          <p className="text-slate-500">SKU: {product.sku_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Image & Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            <div className="aspect-video bg-slate-100 relative">
              {product.tryon_input_image_url ? (
                <img
                  src={product.tryon_input_image_url}
                  alt={product.product_name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                  <ImageIcon className="w-16 h-16 mb-2" />
                  <span>No Try-On image selected</span>
                  <Link
                    to={`/partners/inventory/${productId}/images`}
                    className="mt-2 text-blue-600 hover:underline"
                  >
                    Upload Images →
                  </Link>
                </div>
              )}
              
              {/* Status Badge */}
              <div className="absolute top-4 right-4">
                {product.status === 'ACTIVE' ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                    <XCircle className="w-4 h-4" />
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-slate-200 p-6"
          >
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Product Details</h2>
            
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={editData.product_name}
                    onChange={(e) => setEditData(prev => ({ ...prev, product_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <input
                    type="text"
                    list="inventory-detail-category-suggestions"
                    value={editData.category || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g. Dresses, Cargo Sweatpants, Palazzos…"
                    maxLength={100}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    data-testid="inventory-detail-category"
                  />
                  <datalist id="inventory-detail-category-suggestions">
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
                
                {/* Price & Color Section */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Selling Price (₹)</label>
                    <input
                      type="number"
                      value={editData.selling_price}
                      onChange={(e) => setEditData(prev => ({ ...prev, selling_price: e.target.value ? parseFloat(e.target.value) : '' }))}
                      placeholder="e.g., 1999"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
                    <input
                      type="text"
                      value={editData.color}
                      onChange={(e) => setEditData(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="e.g., Royal Blue"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">MRP (₹)</label>
                    <input
                      type="number"
                      value={editData.mrp}
                      onChange={(e) => setEditData(prev => ({ ...prev, mrp: e.target.value ? parseFloat(e.target.value) : '' }))}
                      placeholder="Original price (optional)"
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fabric</label>
                    <input
                      type="text"
                      value={editData.fabric}
                      onChange={(e) => setEditData(prev => ({ ...prev, fabric: e.target.value }))}
                      placeholder="e.g., 100% Cotton"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Size Chart */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Size Chart
                    <span className="ml-2 text-xs text-slate-400 font-normal">Used for AI size recommendations</span>
                  </label>
                  <SizeChartEditor
                    value={editData.size_chart}
                    onChange={(val) => setEditData(prev => ({ ...prev, size_chart: val }))}
                  />
                </div>

                {/* Color Variants */}
                <ColorVariantsEditor
                  value={editData.color_variants || []}
                  onChange={(next) => setEditData(prev => ({ ...prev, color_variants: next }))}
                />

                {/* AI Color Change toggle */}
                <ColorChangeSection
                  variantCount={(editData.color_variants || []).filter(v => v && v.hex_code).length}
                  enabled={!!editData.color_change_enabled}
                  quality={editData.color_change_quality || 'standard'}
                  onChange={(patch) => setEditData(prev => ({ ...prev, ...patch }))}
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                  >
                    <X className="w-4 h-4 inline mr-1" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4 inline mr-1" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-slate-500">Category</span>
                    <p className="font-medium text-slate-900">{product.category}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Assigned Moment</span>
                    <p className="font-medium text-slate-900">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                        {MOMENTS[product.assigned_moment] || product.assigned_moment}
                      </span>
                    </p>
                  </div>
                </div>
                
                {/* Price & Color Row */}
                <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div>
                    <span className="text-sm text-slate-500">Price</span>
                    <p className="font-semibold text-slate-900">
                      {product.selling_price ? (
                        <>
                          ₹{product.selling_price.toLocaleString()}
                          {product.mrp && product.mrp > product.selling_price && (
                            <span className="ml-2 text-xs text-slate-400 line-through">
                              ₹{product.mrp.toLocaleString()}
                            </span>
                          )}
                        </>
                      ) : product.mrp ? (
                        <>₹{product.mrp.toLocaleString()}</>
                      ) : (
                        <span className="text-amber-600">Not set</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Color</span>
                    <p className="font-medium text-slate-900">
                      {product.color || <span className="text-amber-600">Not set</span>}
                    </p>
                  </div>
                </div>

                {/* Color Variants + AI Color Change status (read-only) */}
                {((product.color_variants || []).length > 0 || product.color_change_enabled) && (
                  <div className="p-3 bg-gradient-to-br from-violet-50/50 to-pink-50/50 rounded-lg border border-violet-100">
                    {(product.color_variants || []).length > 0 && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-slate-500">Available colors</span>
                        <ColorVariantsSwatchRow variants={product.color_variants} max={8} size="sm" />
                        <span className="text-xs text-slate-500">
                          ({product.color_variants.length})
                        </span>
                      </div>
                    )}
                    {product.color_change_enabled && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-violet-500 to-pink-500 text-white">
                        <span>✨ AI Color Change</span>
                        <span className="text-white/80 capitalize">
                          · {product.color_change_quality || 'standard'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {product.fabric && (
                  <div>
                    <span className="text-sm text-slate-500">Fabric</span>
                    <p className="text-slate-900">{product.fabric}</p>
                  </div>
                )}
                
                {product.description && (
                  <div>
                    <span className="text-sm text-slate-500">Description</span>
                    <p className="text-slate-900">{product.description}</p>
                  </div>
                )}

                {/* Size Chart  -  view mode */}
                <div>
                  <span className="text-sm text-slate-500">Size Chart</span>
                  {product.size_chart ? (
                    <div className="mt-2">
                      <SizeChartEditor value={product.size_chart} onChange={() => {}} disabled />
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 mt-0.5">
                      Not set  - {' '}
                      <button
                        onClick={() => setEditing(true)}
                        className="text-blue-600 hover:underline"
                      >
                        add one
                      </button>
                      {' '}to enable AI size recommendations
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                  <div>
                    <span className="text-sm text-slate-500">Images</span>
                    <p className="font-medium text-slate-900">{product.image_count}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Variants</span>
                    <p className="font-medium text-slate-900">{product.variant_count}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Total Stock</span>
                    <p className="font-medium text-slate-900">{product.total_stock}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-slate-200 p-4"
          >
            <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <Link
                to={`/partners/inventory/${productId}/images`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <ImageIcon className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">Manage Images</span>
                <span className="ml-auto text-sm text-slate-400">{product.image_count}</span>
              </Link>
              <Link
                to={`/partners/inventory/${productId}/variants`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Layers className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">Manage Variants</span>
                <span className="ml-auto text-sm text-slate-400">{product.variant_count}</span>
              </Link>
              <Link
                to={`/partners/inventory/${productId}/share-links`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Share2 className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">Share Links</span>
              </Link>
            </div>
          </motion.div>

          {/* Status Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-slate-200 p-4"
          >
            <h3 className="font-semibold text-slate-900 mb-4">Product Status</h3>
            
            {!product.tryon_input_image_key && product.status !== 'ACTIVE' && (
              <div className="mb-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Upload a Try-On image to activate this product
                </p>
              </div>
            )}

            <button
              onClick={handleToggleStatus}
              disabled={!product.tryon_input_image_key && product.status !== 'ACTIVE'}
              className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                product.status === 'ACTIVE'
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {product.status === 'ACTIVE' ? 'Deactivate Product' : 'Activate Product'}
            </button>
          </motion.div>

          {/* Timestamps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-sm text-slate-500 space-y-1"
          >
            <p>Created: {new Date(product.created_at).toLocaleString()}</p>
            <p>Updated: {new Date(product.updated_at).toLocaleString()}</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDetail;
